import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { type PortfolioMatrixJob } from "@/hooks/queries/portfolio/useGetPortfolioMatrix";
import {
  getHealthTier,
  HEALTH_TIER_KEYS,
  healthTierToLabel,
  jobCategoryOptions,
} from "@/lib/constants";

import { type FilterOption } from "./FilterDropdown";

import { JobCategory, JobPriority } from "@/types";

const DIFFICULTY_FILTER_OPTIONS: FilterOption[] = [
  { value: "easy", label: "Easy" },
  { value: "moderate", label: "Moderate" },
  { value: "difficult", label: "Difficult" },
  { value: "very-difficult", label: "Very difficult" },
];

const BOTTLENECK_FILTER_OPTIONS: FilterOption[] = [
  { value: "customer", label: "Customer" },
  { value: "screening", label: "Screening" },
  { value: "sourcing", label: "Sourcing" },
];

// Sentinel filter value for jobs with no priority set yet (null = untriaged).
const PRIORITY_UNSET_FILTER_VALUE = "UNSET";

const PRIORITY_FILTER_OPTIONS: FilterOption[] = [
  { value: JobPriority.HIGH, label: "High" },
  { value: JobPriority.MEDIUM, label: "Medium" },
  { value: JobPriority.LOW, label: "Low" },
  { value: JobPriority.BACKBURNERED, label: "Backburnered" },
  { value: PRIORITY_UNSET_FILTER_VALUE, label: "Unset" },
];

export interface PortfolioFilterControl {
  label: string;
  options: FilterOption[];
  selected: string | null;
  onToggle: (value: string) => void;
  onClear: () => void;
}

// Single-select: choosing the active value again clears it.
const makeToggle =
  (setter: Dispatch<SetStateAction<string | null>>) => (value: string) => {
    setter((prev) => (prev === value ? null : value));
  };

const PORTFOLIO_FILTER_STORAGE_KEY = "portfolio-table-filters:v1";

interface PersistedPortfolioFilters {
  staffer?: string | null;
  sales?: string | null;
}

const readPersistedFilters = (): PersistedPortfolioFilters => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const stored = window.localStorage.getItem(PORTFOLIO_FILTER_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as PersistedPortfolioFilters) : {};
  } catch {
    return {};
  }
};

const persistFilters = (filters: PersistedPortfolioFilters) => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    PORTFOLIO_FILTER_STORAGE_KEY,
    JSON.stringify(filters),
  );
};

/**
 * Owns the Portfolio matrix filter state, derives dynamic option lists from the
 * data, and returns the filtered jobs + ready-to-render filter controls.
 * Single-select per filter.
 */
export const usePortfolioFilters = (jobs: PortfolioMatrixJob[] | undefined) => {
  const { userNameMap, userFirstNameMap } = useTaggableUsers();
  const persistedFilters = useMemo(readPersistedFilters, []);

  const [health, setHealth] = useState<string | null>(null);
  const [difficulty, setDifficulty] = useState<string | null>(null);
  const [bottleneck, setBottleneck] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [priority, setPriority] = useState<string | null>(null);
  const [staffer, setStaffer] = useState<string | null>(
    persistedFilters.staffer ?? null,
  );
  const [sales, setSales] = useState<string | null>(
    persistedFilters.sales ?? null,
  );
  const [jobTitleSearch, setJobTitleSearch] = useState("");

  // Turn anyone on a live req into a list option - even if they're not traditionally a staffer
  const stafferOptions = useMemo<FilterOption[]>(() => {
    const ids = new Set<number>();
    for (const job of jobs ?? []) {
      for (const id of job.stafferIds) ids.add(id);
    }
    return [...ids]
      .map((id) => ({ value: id.toString(), label: userNameMap[id] ?? "" }))
      .filter((option) => option.label !== "")
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [jobs, userNameMap]);

  const salesOptions = useMemo<FilterOption[]>(() => {
    const ids = new Set<number>();
    for (const job of jobs ?? []) {
      for (const id of job.sales) ids.add(id);
    }
    return [...ids]
      .map((id) => ({ value: id.toString(), label: userNameMap[id] ?? "" }))
      .filter((option) => option.label !== "")
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [jobs, userNameMap]);

  useEffect(() => {
    persistFilters({ staffer, sales });
  }, [staffer, sales]);

  // Feeds health card + workload card — filtered by both top-level filters
  const stafferFilteredJobs = useMemo(() => {
    return (jobs ?? []).filter((job) => {
      if (staffer != null && !job.stafferIds.some((id) => id.toString() === staffer)) return false;
      if (sales != null && !job.sales.some((id) => id.toString() === sales)) return false;
      return true;
    });
  }, [jobs, staffer, sales]);

  // Null when no top filter active; single-element set for a specific staffer;
  // job-derived set when filtering by sales (include all staffers on those jobs)
  const scopedStafferIds = useMemo<Set<string> | null>(() => {
    if (staffer == null && sales == null) return null;
    if (staffer != null) return new Set([staffer]);
    const ids = new Set<string>();
    for (const job of stafferFilteredJobs) {
      for (const id of job.stafferIds) ids.add(id.toString());
    }
    return ids;
  }, [stafferFilteredJobs, staffer, sales]);

  const filteredJobs = useMemo(() => {
    const normalizedJobTitleSearch = jobTitleSearch.trim().toLowerCase();

    return (jobs ?? []).filter((job) => {
      if (
        normalizedJobTitleSearch !== "" &&
        !job.title.toLowerCase().includes(normalizedJobTitleSearch)
      ) {
        return false;
      }
      if (health != null && getHealthTier(job.score) !== health) {
        return false;
      }
      if (difficulty != null && job.difficulty !== difficulty) {
        return false;
      }
      if (bottleneck != null && job.bottleneck !== bottleneck) {
        return false;
      }
      if (category != null && job.category !== (category as JobCategory)) {
        return false;
      }
      if (priority != null) {
        if (priority === PRIORITY_UNSET_FILTER_VALUE) {
          if (job.priority !== null) return false;
        } else if (job.priority !== (priority as JobPriority)) {
          return false;
        }
      }
      if (
        staffer != null &&
        !job.stafferIds.some((id) => id.toString() === staffer)
      ) {
        return false;
      }
      if (sales != null && !job.sales.some((id) => id.toString() === sales)) {
        return false;
      }
      return true;
    });
  }, [
    jobs,
    health,
    difficulty,
    bottleneck,
    category,
    priority,
    staffer,
    sales,
    jobTitleSearch,
  ]);

  const filters: PortfolioFilterControl[] = [
    {
      label: "Priority",
      options: PRIORITY_FILTER_OPTIONS,
      selected: priority,
      onToggle: makeToggle(setPriority),
      onClear: () => {
        setPriority(null);
      },
    },
    {
      label: "Job Health",
      options: HEALTH_TIER_KEYS.map((tier) => ({
        value: tier,
        label: healthTierToLabel[tier],
      })),
      selected: health,
      onToggle: makeToggle(setHealth),
      onClear: () => {
        setHealth(null);
      },
    },
    {
      label: "Difficulty",
      options: DIFFICULTY_FILTER_OPTIONS,
      selected: difficulty,
      onToggle: makeToggle(setDifficulty),
      onClear: () => {
        setDifficulty(null);
      },
    },
    {
      label: "Bottleneck",
      options: BOTTLENECK_FILTER_OPTIONS,
      selected: bottleneck,
      onToggle: makeToggle(setBottleneck),
      onClear: () => {
        setBottleneck(null);
      },
    },
    {
      label: "Category",
      options: jobCategoryOptions,
      selected: category,
      onToggle: makeToggle(setCategory),
      onClear: () => {
        setCategory(null);
      },
    },
    {
      label: "Staffer",
      options: stafferOptions,
      selected: staffer,
      onToggle: (value: string) => {
        setStaffer((prev) => (prev === value ? null : value));
        setSales(null);
      },
      onClear: () => {
        setStaffer(null);
      },
    },
    {
      label: "Sales",
      options: salesOptions,
      selected: sales,
      onToggle: (value: string) => {
        setSales((prev) => (prev === value ? null : value));
        setStaffer(null);
      },
      onClear: () => {
        setSales(null);
      },
    },
  ];

  const stafferFirstName =
    staffer != null ? userFirstNameMap[staffer] || null : null;
  const salesFirstName =
    sales != null ? userFirstNameMap[sales] || null : null;

  return {
    filters,
    filteredJobs,
    stafferFilteredJobs,
    staffer,
    sales,
    stafferFirstName,
    salesFirstName,
    scopedStafferIds,
    jobTitleSearch,
    setJobTitleSearch,
  };
};
